import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { postsAPI } from '../services/api';
import { wsService } from '../services/websocket';
import PostCard from '../components/PostCard';
import './PostDetail.css';

interface Comment {
  id: string;
  content: string;
  created_at: string;
  user: {
    id: string;
    username: string;
    display_name: string;
    avatar_url: string;
    trust_rating: number;
  };
}

const PostDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [post, setPost] = useState<any>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (id) {
      loadPost(id);
      loadComments(id);
    }
  }, [id]);

  // Subscribe to live comment updates for this post
  useEffect(() => {
    const handleNewComment = (data: any) => {
      if (data.post_id === id && data.comment) {
        setComments((prev) => {
          // Deduplicate by comment id
          if (prev.some((c) => c.id === data.comment.id)) return prev;
          return [...prev, data.comment];
        });
      }
    };

    wsService.on('new_comment', handleNewComment);
    return () => {
      wsService.off('new_comment', handleNewComment);
    };
  }, [id]);

  const loadPost = async (postId: string) => {
    setLoading(true);
    try {
      const response = await postsAPI.getPost(postId);
      setPost(response.data);
    } catch (err) {
      console.error('Failed to load post:', err);
    }
    setLoading(false);
  };

  const loadComments = async (postId: string) => {
    try {
      const response = await postsAPI.getComments(postId);
      setComments(response.data.comments || []);
    } catch (err) {
      console.error('Failed to load comments:', err);
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !newComment.trim()) return;

    setSubmitting(true);
    try {
      const response = await postsAPI.addComment(id, newComment.trim());
      setComments((prev) => {
        if (prev.some((c) => c.id === response.data.id)) return prev;
        return [...prev, response.data];
      });
      setNewComment('');
    } catch (err) {
      console.error('Failed to add comment:', err);
    }
    setSubmitting(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="post-detail">
        <header className="page-header">
          <Link to="/" className="back-btn">← Back</Link>
          <h2>Post</h2>
        </header>
        <div className="loading-state">Loading post...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="post-detail">
        <header className="page-header">
          <Link to="/" className="back-btn">← Back</Link>
          <h2>Post</h2>
        </header>
        <div className="error-state">Post not found</div>
      </div>
    );
  }

  return (
    <div className="post-detail">
      <header className="page-header">
        <Link to="/" className="back-btn">← Back</Link>
        <h2>Post</h2>
      </header>

      <PostCard post={post} />

      <div className="comments-section">
        <h3>Comments ({comments.length})</h3>

        <form className="comment-form" onSubmit={handleSubmitComment}>
          <textarea
            placeholder="Write a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            maxLength={500}
            rows={2}
          />
          <button
            type="submit"
            className="btn-primary"
            disabled={!newComment.trim() || submitting}
          >
            {submitting ? 'Posting...' : 'Reply'}
          </button>
        </form>

        <div className="comments-list">
          {comments.length === 0 && (
            <div className="empty-state">No comments yet</div>
          )}

          {comments.map((comment) => (
            <div key={comment.id} className="comment">
              <Link to={`/profile/${comment.user.id}`}>
                <img
                  src={comment.user.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${comment.user.username}`}
                  alt={comment.user.display_name}
                  className="avatar"
                />
              </Link>
              <div className="comment-content">
                <div className="comment-header">
                  <Link to={`/profile/${comment.user.id}`} className="comment-user">
                    <span className="display-name">{comment.user.display_name || comment.user.username}</span>
                    <span className="username">@{comment.user.username}</span>
                  </Link>
                  <span className="comment-time">{formatDate(comment.created_at)}</span>
                </div>
                <p className="comment-text">{comment.content}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PostDetail;
